import Client from "@searchkit/instantsearch-client";
import isEqual from "fast-deep-equal/es6";
import { create } from "zustand";
import { fetchFilterPoints } from "../map/searchHits";
import { useProjects } from "../state";
import { config } from "../state/config";
import { getCsrfToken } from "../state/csrf";

// Een eigen searchkit-client, bewust NIET useSearch.client uit state.ts: die
// wrapper schrijft bij elke zoekopdracht naar de globale store, en die stuurt de
// searchResults-laag op de globale kaart aan. Hem hier hergebruiken zou de
// zoekstatus van /search overschrijven.
const original = Client({ url: `${config.searchUrl}/api/search` });

const client = {
  ...original,
  search: async (requests: any[]) => {
    const params = requests?.[0]?.params ?? {};
    const captured: Record<string, any> = {};
    for (const [key, value] of Object.entries(params)) {
      if (/query|filter/i.test(key)) captured[key] = value;
    }
    if (!isEqual(useWorkspaceSearch.getState().params, captured)) {
      useWorkspaceSearch.setState({ params: captured });
    }
    return original.search(requests);
  },
};

// Gelijk aan BULK_PARCEL_LIMIT in ca_project/ca/api.py; de server wijst meer af.
export const BULK_LIMIT = 20;

interface WorkspaceSearchState {
  client: any;
  // De zoekparameters zoals InstantSearch ze opbouwt uit de criteria.
  params?: Record<string, any>;
  // Pas na een klik op een chip tekenen we hits: InstantSearch doet bij het
  // mounten meteen een lege zoekopdracht, en die zou de kaart vullen met alles.
  active: boolean;
  criteria?: Record<string, any>;
  label?: string;
  filterId?: number;
  total?: number;
  error?: string;
  // Loopt op bij elke klik op een chip, ook dezelfde: de kaart zoomt dan opnieuw
  // naar de selectie.
  appliedAt: number;
  apply: (filterId: number, criteria: Record<string, any>, label: string, total?: number) => void;
  clear: () => void;
  setTotal: (total?: number) => void;
  setError: (error?: string) => void;
  // Hele selectie toevoegen aan het project; grens komt van de backend.
  adding: boolean;
  summary?: string;
  addAll: (projectId: number) => Promise<void>;
}

export const useWorkspaceSearch = create<WorkspaceSearchState>((set, get) => ({
  client,
  active: false,
  appliedAt: 0,
  // `params` wordt bewust gewist: die horen nog bij het vórige filter. Pas als
  // InstantSearch met de nieuwe criteria heeft gezocht staan ze er weer, en dán
  // pas mag de kaart zoomen — anders zoomt hij naar de oude (of lege) selectie.
  // `total` is what the server measured when proposing: showing it right away
  // avoids a flicker of "Zoeken…" for a number that already arrived. The map
  // measures again afterwards, because the index does not stand still.
  apply: (filterId, criteria, label, total) =>
    set((s) => ({
      filterId,
      criteria,
      label,
      active: true,
      params: undefined,
      total,
      error: undefined,
      summary: undefined,
      appliedAt: s.appliedAt + 1,
    })),
  clear: () =>
    set({
      active: false,
      criteria: undefined,
      label: undefined,
      filterId: undefined,
      total: undefined,
      error: undefined,
    }),
  setTotal: (total) => set({ total, error: undefined }),
  setError: (error) => set({ error }),

  adding: false,
  addAll: async (projectId: number) => {
    const { client, params, total, adding } = get();
    if (adding || !params || !total) return;
    set({ adding: true, summary: undefined, error: undefined });
    try {
      const points = await fetchFilterPoints(client, params, BULK_LIMIT);
      const response = await fetch(`/api/projects/${projectId}/parcels/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
        body: JSON.stringify({ points }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.detail || `HTTP ${response.status}`);

      const parts = [`${body.added.length} toegevoegd`];
      if (body.skipped.length) parts.push(`${body.skipped.length} stond er al`);
      if (body.failed) parts.push(`${body.failed} mislukt`);
      set({ summary: parts.join(", ") });
      // Percelen, kaartlaag en chatcontext lopen allemaal via de projectenstore.
      useProjects.getState().fetchProjects(true);
    } catch (e: any) {
      set({ error: e?.message || "Toevoegen mislukt" });
    } finally {
      set({ adding: false });
    }
  },
}));
